import {
  faBolt,
  faDiamond,
  faGear,
  faHouse,
  faPersonRunning,
  faRoad,
  faSuitcase,
  faTableList,
  faTrailer,
  faTrophy,
  faTruck,
  faTruckMoving,
} from '@fortawesome/free-solid-svg-icons';
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import { EmployeeScheduleResponse, UserModel } from 'api/model';
import { amber, black, lightGrey } from 'constants/Colors';
import { UserFormField } from 'pages/Employees/useUserForm';

/** Svart = behörig, bärnsten = behörig bara i nödfall, grå = inte behörig. */
const capabilityColor = (enabled?: boolean, emergency?: boolean) =>
  enabled ? (emergency ? amber : black) : lightGrey;

const EMERGENCY_SUFFIX = ' — bara i nödfall';

/** Ikonens egen titel vinner över omslagets, så nödfallet måste stå i den (granskning 2026-10-12). */
const emergencyTitle = (title: string, emergency?: boolean) =>
  emergency ? `${title}${EMERGENCY_SUFFIX}` : title;

const userPropsIconDictionary = {
  [UserFormField.canTestTrucks]: {
    icon: (enabled?: boolean, emergency?: boolean) => (
      <span
        className="fa-layers fa-fw"
        key="canTestTrucksIcon"
        title={emergencyTitle(
          'Godkänd för test av transportbilar (vid inköp/inbyte)',
          emergency
        )}
      >
        <FontAwesomeIcon
          color={capabilityColor(enabled, emergency)}
          icon={faTruck}
        />
        <FontAwesomeIcon
          color="white"
          icon={faTableList}
          transform="shrink-8 "
        />
      </span>
    ),
    title: 'Godkänd för test av transportbilar (vid inköp/inbyte)',
  },
  [UserFormField.canDoHomeDelivery]: {
    icon: (enabled?: boolean, emergency?: boolean) => (
      <FontAwesomeIcon
        color={capabilityColor(enabled, emergency)}
        icon={faHouse}
        title={emergencyTitle('Godkänd för hemleverans', emergency)}
      />
    ),
    title: 'Godkänd för hemleverans',
  },
  [UserFormField.isRunner]: {
    icon: (enabled?: boolean) => (
      <FontAwesomeIcon
        color={enabled ? black : lightGrey}
        icon={faPersonRunning}
        title="Runner – ingen kundkontakt (inga kundben i inköp, inbyte, auktion eller hemleverans)"
      />
    ),
    title: 'Runner – ingen kundkontakt',
  },
  [UserFormField.canDriveElectric]: {
    icon: (enabled?: boolean, emergency?: boolean) => (
      <FontAwesomeIcon
        color={capabilityColor(enabled, emergency)}
        icon={faBolt}
      />
    ),
    title: 'Godkänd för elbilar',
  },
  [UserFormField.canDriveManual]: {
    icon: (enabled?: boolean, emergency?: boolean) => (
      <FontAwesomeIcon
        color={capabilityColor(enabled, emergency)}
        icon={faGear}
        title={emergencyTitle('Körkort för manuell växel', emergency)}
      />
    ),
    title: 'Körkort för manuell växel',
  },
  [UserFormField.canTestPremiumCars]: {
    icon: (enabled?: boolean, emergency?: boolean) => (
      <span
        className="fa-layers fa-fw"
        title={emergencyTitle(
          'Godkänd för test av premiumbilar (vid inköp/inbyte)',
          emergency
        )}
      >
        <FontAwesomeIcon
          color={capabilityColor(enabled, emergency)}
          icon={faTrophy}
        />
        <FontAwesomeIcon
          color="white"
          icon={faTableList}
          transform="shrink-8"
        />
      </span>
    ),
    title: 'Godkänd för test av premiumbilar (vid inköp/inbyte)',
  },
  [UserFormField.canDriveTrucks]: {
    icon: (enabled?: boolean) => (
      <FontAwesomeIcon
        color={enabled ? black : lightGrey}
        icon={faTruck}
        title="C-KORT"
      />
    ),
    title: 'C-KORT',
  },
  [UserFormField.canDriveCarTransportTrucks]: {
    icon: (enabled?: boolean) => (
      <FontAwesomeIcon
        color={enabled ? black : lightGrey}
        icon={faTruckMoving}
        title="Godkänd för biltransporter med lastbil"
      />
    ),
    title: 'Godkänd för biltransporter med lastbil',
  },
  [UserFormField.canDriveTrailers]: {
    icon: (enabled?: boolean) => (
      <FontAwesomeIcon
        color={enabled ? black : lightGrey}
        icon={faTrailer}
        title="Godkänd för släpvagn (BE-KORT)"
      />
    ),
    title: 'Godkänd för släpvagn (BE-KORT)',
  },
  [UserFormField.canDrivePremium]: {
    icon: (enabled?: boolean, emergency?: boolean) => (
      <FontAwesomeIcon
        color={capabilityColor(enabled, emergency)}
        icon={faTrophy}
        title={emergencyTitle('Godkänd för premiumbilar', emergency)}
      />
    ),
    title: 'Godkänd för premiumbilar',
  },
  [UserFormField.canDoLongDrives]: {
    icon: (enabled?: boolean, emergency?: boolean) => (
      <FontAwesomeIcon
        color={capabilityColor(enabled, emergency)}
        icon={faRoad}
        title={emergencyTitle('Godkänd för långkörning', emergency)}
      />
    ),
    title: 'Godkänd för långkörning',
  },
  [UserFormField.isFullTime]: {
    icon: (enabled?: boolean) => (
      <FontAwesomeIcon
        color={enabled ? black : lightGrey}
        icon={faDiamond}
        title="Heltidsanställd"
      />
    ),
    title: 'Heltidsanställd',
  },
  [UserFormField.certifiedToCallCustomers]: {
    icon: (enabled?: boolean) => (
      <div
        style={{
          width: 20,
          height: 20,
        }}
        title="Certifierad för att ringa kunder"
      >
        <img
          alt="Telefon"
          height={20}
          src={
            enabled
              ? '/images/rotary-phone-red.png'
              : '/images/rotary-phone-grey.png'
          }
          width={20}
        />
      </div>
    ),
    title: 'Certifierad för att ringa kunder',
  },
  [UserFormField.canStayAtHotels]: {
    icon: (enabled?: boolean) => (
      <FontAwesomeIcon
        color={enabled ? black : lightGrey}
        icon={faSuitcase}
        title="Kan övernatta på hotell"
      />
    ),
    title: 'Kan övernatta på hotell',
  },
};

type UserPropsIcon = {
  enabled?: boolean;
  icon: React.ReactElement<FontAwesomeIconProps>;
  title: string;
  field: UserFormField;
};

export const userPropsArray = [
  UserFormField.isFullTime,
  UserFormField.canTestTrucks,
  UserFormField.canTestPremiumCars,
  UserFormField.canDoHomeDelivery,
  UserFormField.isRunner,
  UserFormField.canDriveElectric,
  UserFormField.canDriveManual,
  UserFormField.canDriveTrucks,
  UserFormField.canDriveCarTransportTrucks,
  UserFormField.canDriveTrailers,
  UserFormField.canDrivePremium,
  UserFormField.canDoLongDrives,
  UserFormField.certifiedToCallCustomers,
  UserFormField.canStayAtHotels,
];

export const getUserPropsIconArray = (
  user: UserModel | EmployeeScheduleResponse
) => {
  const icons: UserPropsIcon[] = [
    {
      enabled: user.canTestPremiumCars,

      icon: userPropsIconDictionary[UserFormField.canTestPremiumCars].icon(
        user.canTestPremiumCars,
        user.testPremiumEmergencyOnly
      ),
      title:
        userPropsIconDictionary[UserFormField.canTestPremiumCars].title +
        (user.canTestPremiumCars && user.testPremiumEmergencyOnly
          ? EMERGENCY_SUFFIX
          : ''),
      field: UserFormField.canTestPremiumCars,
    },
    {
      enabled: user.canTestTrucks,
      icon: userPropsIconDictionary[UserFormField.canTestTrucks].icon(
        user.canTestTrucks,
        user.testTrucksEmergencyOnly
      ),
      title:
        userPropsIconDictionary[UserFormField.canTestTrucks].title +
        (user.canTestTrucks && user.testTrucksEmergencyOnly
          ? EMERGENCY_SUFFIX
          : ''),
      field: UserFormField.canTestTrucks,
    },
    {
      enabled: user.canDoHomeDelivery,
      icon: userPropsIconDictionary[UserFormField.canDoHomeDelivery].icon(
        user.canDoHomeDelivery,
        user.homeDeliveryEmergencyOnly
      ),
      title:
        userPropsIconDictionary[UserFormField.canDoHomeDelivery].title +
        (user.canDoHomeDelivery && user.homeDeliveryEmergencyOnly
          ? EMERGENCY_SUFFIX
          : ''),
      field: UserFormField.canDoHomeDelivery,
    },
    {
      enabled: user.isRunner,
      icon: userPropsIconDictionary[UserFormField.isRunner].icon(user.isRunner),
      title: userPropsIconDictionary[UserFormField.isRunner].title,
      field: UserFormField.isRunner,
    },
    {
      enabled: user.canDriveElectric,
      icon: userPropsIconDictionary[UserFormField.canDriveElectric].icon(
        user.canDriveElectric,
        user.electricEmergencyOnly
      ),
      title:
        userPropsIconDictionary[UserFormField.canDriveElectric].title +
        (user.canDriveElectric && user.electricEmergencyOnly
          ? EMERGENCY_SUFFIX
          : ''),
      field: UserFormField.canDriveElectric,
    },
    {
      enabled: user.canDriveManual,
      icon: userPropsIconDictionary[UserFormField.canDriveManual].icon(
        user.canDriveManual,
        user.manualEmergencyOnly
      ),
      title:
        userPropsIconDictionary[UserFormField.canDriveManual].title +
        (user.canDriveManual && user.manualEmergencyOnly
          ? EMERGENCY_SUFFIX
          : ''),
      field: UserFormField.canDriveManual,
    },
    {
      enabled: user.canDriveTrucks,

      icon: userPropsIconDictionary[UserFormField.canDriveTrucks].icon(
        user.canDriveTrucks
      ),
      title: userPropsIconDictionary[UserFormField.canDriveTrucks].title,
      field: UserFormField.canDriveTrucks,
    },
    {
      enabled: !!user.canDriveCarTransportTrucks,
      icon: userPropsIconDictionary[
        UserFormField.canDriveCarTransportTrucks
      ].icon(user.canDriveCarTransportTrucks),
      title:
        userPropsIconDictionary[UserFormField.canDriveCarTransportTrucks].title,
      field: UserFormField.canDriveCarTransportTrucks,
    },
    {
      enabled: user.canDriveTrailers,
      icon: userPropsIconDictionary[UserFormField.canDriveTrailers].icon(
        user.canDriveTrailers
      ),
      title: userPropsIconDictionary[UserFormField.canDriveTrailers].title,
      field: UserFormField.canDriveTrailers,
    },
    {
      enabled: user.canDrivePremium,
      icon: userPropsIconDictionary[UserFormField.canDrivePremium].icon(
        user.canDrivePremium,
        user.premiumEmergencyOnly
      ),
      title:
        userPropsIconDictionary[UserFormField.canDrivePremium].title +
        (user.canDrivePremium && user.premiumEmergencyOnly
          ? EMERGENCY_SUFFIX
          : ''),
      field: UserFormField.canDrivePremium,
    },
    {
      enabled: user.canDoLongDrives,
      icon: userPropsIconDictionary[UserFormField.canDoLongDrives].icon(
        user.canDoLongDrives,
        user.longDrivesEmergencyOnly
      ),
      title:
        userPropsIconDictionary[UserFormField.canDoLongDrives].title +
        (user.canDoLongDrives && user.longDrivesEmergencyOnly
          ? EMERGENCY_SUFFIX
          : ''),
      field: UserFormField.canDoLongDrives,
    },
    {
      enabled: user.isFullTime,
      icon: userPropsIconDictionary[UserFormField.isFullTime].icon(
        user.isFullTime
      ),
      title: userPropsIconDictionary[UserFormField.isFullTime].title,
      field: UserFormField.isFullTime,
    },
    {
      enabled: user.certifiedToCallCustomers,
      icon: userPropsIconDictionary[
        UserFormField.certifiedToCallCustomers
      ].icon(user.certifiedToCallCustomers),
      title:
        userPropsIconDictionary[UserFormField.certifiedToCallCustomers].title,
      field: UserFormField.certifiedToCallCustomers,
    },
    {
      enabled: user.canStayAtHotels,
      icon: userPropsIconDictionary[UserFormField.canStayAtHotels].icon(
        user.canStayAtHotels
      ),
      title: userPropsIconDictionary[UserFormField.canStayAtHotels].title,
      field: UserFormField.canStayAtHotels,
    },
  ];
  return icons;
};

export default userPropsIconDictionary;
